import { Amplify } from "aws-amplify"
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito"
import { CookieStorage } from "aws-amplify/utils"

// Amplify Auth against the console-managed Cognito pool, with the session
// in parent-domain cookies (Domain=newfangledaudio.com) so my./buy./www.
// (the widget) and the login app share one sign-in. Sign-in itself happens
// on the login app (REACT_APP_LOGIN_URL); this app only reads and refreshes
// the session.
//
// makeSharedCookieStorage is duplicated in lib/web/login and lib/web/buy
// (CRA forbids imports from outside src/) — keep the copies identical:
// Domain only when the host is under it (a staging build on localhost gets
// a host-only cookie, so local dev works and apps on different dev ports
// still share), Secure only on https (Safari drops Secure cookies on
// http://localhost), SameSite=Lax, 30 days ≈ the refresh token validity.
export function makeSharedCookieStorage(configuredDomain: string | undefined): CookieStorage {
  const hostname = window.location.hostname
  const domain = configuredDomain && (hostname === configuredDomain || hostname.endsWith("." + configuredDomain))
    ? configuredDomain
    : undefined
  return new CookieStorage({
    domain,
    path: "/",
    expires: 30,
    sameSite: "lax",
    secure: window.location.protocol === "https:",
  })
}

// The pre-Amplify session (oidc-client-ts) lived in chunked cookies named
// nfa_<key>.<n> on the same Domain. They would otherwise ride along to
// api.newfangledaudio.com for up to 30 days, so delete them once — with the
// same attribute set they were written with, or the browser treats the
// delete as a different cookie.
export function purgeLegacyOidcCookies(configuredDomain: string | undefined) {
  const hostname = window.location.hostname
  const domain = configuredDomain && (hostname === configuredDomain || hostname.endsWith("." + configuredDomain))
    ? configuredDomain
    : undefined
  const attributes = "; Path=/; Max-Age=0; SameSite=Lax"
    + (window.location.protocol === "https:" ? "; Secure" : "")
    + (domain ? `; Domain=${domain}` : "")
  for (const part of document.cookie.split("; ")) {
    const name = part.slice(0, part.indexOf("="))
    if (name.startsWith("nfa_"))
      document.cookie = `${name}=${attributes}`
  }
}

let configured = false

export function configureAmplify() {
  if (configured)
    return
  configured = true
  const cookieDomain = process.env.REACT_APP_COOKIE_DOMAIN || undefined
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: process.env.REACT_APP_USER_POOL_ID!,
        userPoolClientId: process.env.REACT_APP_USER_POOL_APP_CLIENT_ID!,
        loginWith: { username: true, email: true },
      },
    },
  })
  cognitoUserPoolsTokenProvider.setKeyValueStorage(makeSharedCookieStorage(cookieDomain))
  purgeLegacyOidcCookies(cookieDomain)
}

export const LOGIN_URL: string = process.env.REACT_APP_LOGIN_URL ?? ""

// The login app's sign-in page, bringing the user back to `returnTo`
// (defaults to the current page).
export function loginPageUrl(returnTo: string = window.location.href): string {
  return `${LOGIN_URL}/login?returnTo=${encodeURIComponent(returnTo)}`
}
